import React, { useEffect, useMemo, useState } from 'react';
import Style from '../Screens/doctors/Doctors.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Box,
} from '@mui/material';
import DeleteDialog from '../components/DeleteDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';
import SearchClinic from '../components/Autocomplete/SearchClinic';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { addAdditionalService, deleteAdditionalService, getAdditionalServices, updateAdditionalService } from '../apis/additionalServiceSlice';
import CommonAgGrid from '../components/CommonAgGrid/CommonAgGrid';

export default function AdditionalService() {
  const dispatch = useDispatch();
  const { services, loading, totalCount } = useSelector(
    (state) => state.additionalServiceData || { services: [], loading: false, totalCount: 0 },
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Popup states
  const [openPopup, setOpenPopup] = useState(false);
  const [formData, setFormData] = useState({ clinicId: null, name: '' });
  const [editId, setEditId] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  // Get clinic ID from auth (assuming it's in the Redux store)
  const { userDetails } = useSelector((state) => state.authData || {});

  useEffect(() => {
    dispatch(getAdditionalServices({ page, pageSize }));
  }, [dispatch, page, pageSize]);

  // Open Add popup
  const handleOpenAdd = () => {
    setEditId(null); // reset edit mode
    setFormData({ clinicId: null, name: '' }); // clear input
    setOpenPopup(true);
  };

  // Open Edit popup
  const handleOpenEdit = (row) => {
    setEditId(row._id); // store id for update
    setFormData({ clinicId: row.clinicId?._id, name: row.name });
    setOpenPopup(true);
  };

  // Submit Add / Update
  const handleSubmit = async () => {
    if (!formData.clinicId) {
      toast.error('Clinic is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    let response;

    if (editId) {
      // UPDATE
      response = await dispatch(
        updateAdditionalService({ id: editId, name: formData.name, clinicId: formData.clinicId }),
      );
    } else {
      // ADD
      response = await dispatch(addAdditionalService({ name: formData.name, clinicId: formData.clinicId }));
    }

    if (response?.payload?.success) {
      toast.success(response.payload.message);
      setOpenPopup(false); // close popup
      dispatch(getAdditionalServices({ page, pageSize }));
    } else {
      toast.error(response?.payload?.message || 'Error');
    }
  };

  const handleDelete = async (row) => {
    const response = await dispatch(deleteAdditionalService(row._id));

    if (response?.payload?.success) {
      toast.success(response.payload.message);
      dispatch(getAdditionalServices({ page, pageSize }));
    } else {
      toast.error('Error deleting service');
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    await handleDelete(deleteRow);
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.data)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.data)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
      {
        field: 'clinicName',
        headerName: 'Clinic Name',
        minWidth: 180,
        valueGetter: (params) => params.data?.clinicId?.name || 'N/A',
      },
      { field: 'name', headerName: 'Service Name', minWidth: 220 },
    ],
    [],
  );

  const disabled = !formData.clinicId || !formData.name.trim();

  return (
    <div className={Style.mainDataTable}>
      <DeleteDialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <Card className={Style.tableCard}>
        <div className={Style.tableHeader}>
          <h2 className={Style.tableTitle}>Additional Services</h2>
            <Button
              className={Style.addBtn}
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
            >
              Add Service
            </Button>
        </div>
        <CommonAgGrid
          rowData={services || []}
          columnDefs={columns}
          getRowId={(p) => String(p.data?._id)}
          loading={loading}
          height={280}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
        />
      </Card>

      {/* Popup */}
      <Dialog
        open={openPopup}
        fullWidth
        maxWidth={'sm'}
        onClose={() => setOpenPopup(false)}
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border-soft)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'var(--color-heading)', borderBottom: '1px solid var(--color-border-soft)' }}>
          {editId ? 'Edit Service' : 'Add Service'}
        </DialogTitle>

        <DialogContent
          sx={{
            '& .MuiInputBase-root': { fontSize: '0.84rem' },
            '& .MuiInputLabel-root': {
              fontSize: '0.8rem',
              color: 'var(--color-text-muted)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--color-heading)',
            },
          }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>

          <SearchClinic
            open={openPopup}
            setData={setFormData}
            data={formData}
            name="clinicId"
            label="Select Clinic"
            variant="outlined"
            // size="small"
          />
          <TextField
            margin="dense"
            required
            label="Service Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={{ marginTop: '1rem' }}
          />
            </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid var(--color-border-soft)', p: 1.25 }}>
          <Button
            onClick={() => setOpenPopup(false)}
            sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid var(--color-border-soft)', color: 'var(--color-heading)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            className={disabled ? "dialogDisableBtn" : "dialogSubmitBtn"}
            disabled={disabled}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
          >
            {editId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
